import NStatusChip from "@/components/common/NStatusChip";
import RecommendationChip from "@/components/common/RecommendationChip";
import { isFieldTrial } from "@/lib/utils/access";
import { getSimulationTypeLabel } from "@/lib/utils/nitrogenPlan";
import { roundTo } from "@/lib/utils/numbers";
import { FieldInsights } from "@/types/fieldInsights";
import { ProcessingStatus } from "@/types/fieldNitrogenPlan";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import {
    Button,
    Chip,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
} from "@heroui/react";
import { ReactNode } from "react";
import { useI18n } from "@/lib/i18n/useI18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLeaf, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { getRateValue, FormatUnit } from "@/components/home/InsightsCells";
import { Link } from "@tanstack/react-router";

type FormatDateMMDDYYYY = ReturnType<typeof useI18n>["formatDateMMDDYYYY"];

interface DetailRow {
    label: string;
    value: ReactNode;
}

type InsightsMobileFieldDetailDrawerProps = {
    field: FieldInsights | null;
    isOpen: boolean;
    onClose: () => void;
    growersById: Record<number, Organization>;
    serviceProvidersById: Record<number, Organization>;
    accountManagersById: Record<number, User>;
    isConsultantOrSentinel: boolean;
    userIsSentinel: boolean;
    user: User | null | undefined;
    formatDateMMDDYYYY: FormatDateMMDDYYYY;
    formatUnit: FormatUnit;
    showAdvisor: boolean;
    onOpenAdvisor: (fieldId: number | undefined) => void;
    viewAllRates: boolean;
};

function DetailRowItem({ label, value }: DetailRow) {
    return (
        <div className="flex flex-row justify-between items-start gap-4 py-2 border-b border-gray-100 last:border-0">
            <div className="text-sm text-gray-600 shrink-0">{label}</div>
            <div className="text-sm text-right font-medium">
                {value ?? "--"}
            </div>
        </div>
    );
}

export default function InsightsMobileFieldDetailDrawer({
    field,
    isOpen,
    onClose,
    growersById,
    serviceProvidersById,
    accountManagersById,
    isConsultantOrSentinel,
    userIsSentinel,
    user,
    formatDateMMDDYYYY,
    formatUnit,
    showAdvisor,
    onOpenAdvisor,
    viewAllRates,
}: InsightsMobileFieldDetailDrawerProps) {
    if (!field) return null;

    const growerName = growersById[field.grower]?.name;
    const serviceProviderName =
        serviceProvidersById[field.service_provider]?.name;
    const accountManagerName =
        accountManagersById[field.account_manager]?.full_name;

    const trialField = !!user && isFieldTrial(field, user);

    const recommendation = field.recommendation;

    const cropName =
        field.active_planting_operation?.crop_configuration?.crop_name;

    const imageDate =
        recommendation?.latest_image_capture_date ??
        field.latest_recommendation_date;

    const plan = field.default_nitrogen_plan_simulation;

    const water = field.water;
    const waterEstimate = water?.water_estimate;

    const shouldShowRateDetails =
        !trialField && (recommendation.recommend_fertigation || viewAllRates);

    const rateValue = getRateValue({
        trialField,
        recommendation,
        viewAllRates,
        plan,
        formatUnit,
    });

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            placement="bottom"
            size="lg"
            scrollBehavior="inside"
            classNames={{
                base: "h-[80vh] max-h-[80vh]",
            }}
        >
            <DrawerContent>
                <DrawerHeader>
                    <div className="flex flex-col items-start">
                        <div className="font-semibold text-lg">
                            {field.name}
                        </div>

                        {growerName && (
                            <div className="text-xs text-gray-600">
                                {growerName}
                            </div>
                        )}
                    </div>
                </DrawerHeader>

                <DrawerBody>
                    <div className="flex flex-col">
                        {isConsultantOrSentinel && (
                            <DetailRowItem
                                label="Grower"
                                value={growerName ?? "--"}
                            />
                        )}
                        {userIsSentinel && (
                            <>
                                <DetailRowItem
                                    label="CSP"
                                    value={serviceProviderName ?? "--"}
                                />
                                <DetailRowItem
                                    label="Account Manager"
                                    value={accountManagerName ?? "--"}
                                />
                            </>
                        )}
                        <DetailRowItem
                            label="Image Date"
                            value={
                                imageDate ? formatDateMMDDYYYY(imageDate) : "--"
                            }
                        />
                        <DetailRowItem
                            label="Recommendation"
                            value={
                                trialField ? (
                                    <Chip
                                        color="warning"
                                        variant="flat"
                                        radius="sm"
                                    >
                                        Trial Field
                                    </Chip>
                                ) : (
                                    <RecommendationChip
                                        recommendation={recommendation}
                                        notes={
                                            recommendation.user_recommendation
                                                ?.notes ?? undefined
                                        }
                                        user={
                                            recommendation.user_recommendation
                                                ?.user ?? undefined
                                        }
                                        deferDoNotApply={true}
                                    />
                                )
                            }
                        />
                        <DetailRowItem
                            label="N Status"
                            value={
                                !trialField && recommendation?.n_status ? (
                                    <NStatusChip
                                        nStatus={recommendation.n_status}
                                    />
                                ) : (
                                    "--"
                                )
                            }
                        />
                        <DetailRowItem
                            label="N Sufficiency"
                            value={
                                recommendation?.si_mean !== null &&
                                recommendation?.si_mean !== undefined
                                    ? `${roundTo(recommendation.si_mean * 100, 1)}%`
                                    : "--"
                            }
                        />
                        <DetailRowItem label="Crop" value={cropName ?? "--"} />
                        <DetailRowItem
                            label="Growth Stage"
                            value={field.crop_stage?.growth_stage ?? "--"}
                        />
                        <DetailRowItem
                            label="N Applied"
                            value={`${field.n_applied_so_far ?? 0} ${formatUnit("lb_ac", "kg_ha")}`}
                        />
                        <DetailRowItem label="Rate" value={rateValue} />
                        {shouldShowRateDetails &&
                            plan &&
                            plan.processing_status ===
                                ProcessingStatus.COMPLETED && (
                                <>
                                    <DetailRowItem
                                        label="Scenario"
                                        value={getSimulationTypeLabel(
                                            plan.simulation_type,
                                        )}
                                    />
                                    <DetailRowItem
                                        label="Target Growth Stage"
                                        value={plan.target_growth_stage_label}
                                    />
                                </>
                            )}
                        <DetailRowItem
                            label="Water Status"
                            value={waterEstimate?.moisture_status ?? "--"}
                        />
                        <DetailRowItem
                            label="Water Sufficiency"
                            value={
                                waterEstimate?.capacity_percentage !== undefined
                                    ? `${waterEstimate.capacity_percentage.toFixed(1)}%`
                                    : "--"
                            }
                        />
                        <DetailRowItem
                            label="Current Deficit"
                            value={
                                waterEstimate?.deficit !== undefined
                                    ? `${waterEstimate.deficit > 0 ? "+" : ""}${waterEstimate.deficit.toFixed(2)} ${formatUnit("in", "mm")}`
                                    : "--"
                            }
                        />
                        <DetailRowItem
                            label="7-Day Forecast Deficit"
                            value={
                                waterEstimate?.deficit_forecast !== undefined
                                    ? `${waterEstimate.deficit_forecast > 0 ? "+" : ""}${waterEstimate.deficit_forecast.toFixed(2)} ${formatUnit("in", "mm")}`
                                    : "--"
                            }
                        />
                        <DetailRowItem
                            label="Rain Forecast"
                            value={
                                water?.expectedRain !== undefined
                                    ? `${water.expectedRain.toFixed(2)} ${formatUnit("in", "mm")}`
                                    : "--"
                            }
                        />
                        <DetailRowItem
                            label="Total Irrigation"
                            value={
                                water?.cumulativeIrrigation !== undefined
                                    ? `${water.cumulativeIrrigation.toFixed(2)} ${formatUnit("in", "mm")}`
                                    : "--"
                            }
                        />
                    </div>
                </DrawerBody>

                <DrawerFooter className="flex flex-col gap-2">
                    {showAdvisor && !trialField && (
                        <Button
                            fullWidth
                            color="primary"
                            variant="flat"
                            startContent={<FontAwesomeIcon icon={faLeaf} />}
                            isDisabled={!field.id}
                            onPress={() => {
                                onOpenAdvisor(field.id);
                                onClose();
                            }}
                        >
                            Open Advisor
                        </Button>
                    )}

                    <Link
                        to="/dashboard/fields/$fieldId/insights"
                        params={{
                            fieldId: field.id.toString(),
                        }}
                        search={{
                            advisor: undefined,
                        }}
                        onClick={onClose}
                        className="w-full"
                    >
                        <Button
                            as="span"
                            fullWidth
                            color="primary"
                            variant="flat"
                            startContent={
                                <FontAwesomeIcon icon={faUpRightFromSquare} />
                            }
                        >
                            Open Field
                        </Button>
                    </Link>

                    <Button fullWidth color="primary" onPress={onClose}>
                        Close
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
